import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faUsers,
  faSpinner,
  faSearch,
  faEdit,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const statusBadge = (status) => {
  const map = {
    active: { icon: faCircleCheck, label: 'Actif', cls: 'bg-green-100 text-green-700' },
    suspended: { icon: faCircleXmark, label: 'Suspendu', cls: 'bg-red-100 text-red-700' },
    inactive: { icon: faCircleExclamation, label: 'Inactif', cls: 'bg-gray-100 text-gray-700' },
  };
  const s = map[status] || map.inactive;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.cls}`}>
      <FontAwesomeIcon icon={s.icon} />
      {s.label}
    </span>
  );
};

const SupervisorsCatiList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ page: currentPage, per_page: 10 });
        if (debouncedSearch) params.append('search', debouncedSearch);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        const res = await api.get(`/supervisor-catis?${params.toString()}`);
        setItems(res.data.data || []);
        setTotalPages(res.data.last_page || 1);
        setTotalCount(res.data.total || 0);
        setError('');
      } catch (e) {
        setError(e.response?.data?.message || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [currentPage, debouncedSearch, statusFilter]);

  return (
    <div className="p-4 sm:p-6">
      {/* Header: stacks on mobile, inline on ≥sm */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <FontAwesomeIcon icon={faUsers} className="text-2xl text-red-600 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Superviseurs CATI</h1>
            <p className="text-sm text-gray-600">{totalCount} superviseur(s)</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/supervisors-cati/create')}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 w-full sm:w-auto"
        >
          <FontAwesomeIcon icon={faPlus} />
          Nouveau
        </button>
      </div>

      {/* Filters: stack on mobile */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 min-w-0">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="suspended">Suspendu</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
      )}

      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl mb-2" />
          <div>Chargement...</div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Aucun superviseur CATI trouvé.
        </div>
      ) : (
        <>
          {/* Card list on mobile (<sm) */}
          <div className="sm:hidden space-y-3">
            {items.map((u) => (
              <div key={u.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900 truncate">{u.name}</div>
                    <div className="text-xs text-gray-600 truncate">{u.email}</div>
                  </div>
                  {statusBadge(u.status)}
                </div>
                <div className="text-xs text-gray-500 space-y-0.5 mb-3">
                  <div>
                    <span className="font-medium text-gray-700">Créé par: </span>
                    {u.created_by || '-'}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Dernière connexion: </span>
                    {u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : 'Jamais'}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/supervisors-cati/${u.id}`)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Modifier
                </button>
              </div>
            ))}
          </div>

          {/* Table on ≥sm */}
          <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">Créé par</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden lg:table-cell">Dernière connexion</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 break-all">{u.email}</td>
                      <td className="px-4 py-3">{statusBadge(u.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{u.created_by || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                        {u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : 'Jamais'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/supervisors-cati/${u.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          <span className="hidden md:inline">Modifier</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SupervisorsCatiList;
